// @ts-nocheck
// TECH DEBT: needs typed discriminated union for EmailBlock.props
// (heading | text | button | image | spacer | signature) before strict mode can pass.
// Tracked: refactor scheduled for Sprint 6 (component decomposition).
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProject } from '../../contexts/ProjectContext';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  PaperAirplaneIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { Button, Card, Input, Select, Modal, Badge } from '@realvisor/ui';

// ============================================
// TYPY
// ============================================

interface EmailBlock {
  id: string;
  type: string;
  content?: string;
  props?: Record<string, any>;
}

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: 'transactional' | 'sequence' | 'manual' | 'system';
  subject: string;
  preheader?: string;
  blocks: EmailBlock[];
  replyTo?: string;
  fromName?: string;
  isActive: boolean;
  isDefault: boolean;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
}

// ============================================
// DOSTUPNÉ PROMĚNNÉ
// ============================================

const AVAILABLE_VARIABLES = [
  {
    category: 'Kontakt',
    icon: '👤',
    variables: [
      { key: 'contact.firstName', label: 'Jméno', example: 'Jan' },
      { key: 'contact.lastName', label: 'Příjmení', example: 'Novák' },
      { key: 'contact.fullName', label: 'Celé jméno', example: 'Jan Novák' },
      { key: 'contact.email', label: 'Email', example: 'jan@example.cz' },
      { key: 'contact.phone', label: 'Telefon', example: '+420 123 456 789' },
      { key: 'contact.salutation', label: 'Oslovení', example: 'Dobrý den, Jene' },
    ],
  },
  {
    category: 'Nemovitost',
    icon: '🏠',
    variables: [
      { key: 'property.typeLabel', label: 'Typ nemovitosti', example: 'Byt' },
      { key: 'property.address', label: 'Adresa', example: 'Václavské náměstí 1' },
      { key: 'property.addressFull', label: 'Plná adresa', example: 'Václavské náměstí 1, Praha 1' },
      { key: 'property.floorArea', label: 'Plocha (m²)', example: '75' },
      { key: 'property.localType', label: 'Dispozice', example: '3+kk' },
      { key: 'property.city', label: 'Město', example: 'Praha' },
      { key: 'property.ratingLabel', label: 'Stav', example: 'Velmi dobrý' },
    ],
  },
  {
    category: 'Ocenění',
    icon: '💰',
    variables: [
      { key: 'valuation.avgPrice', label: 'Průměrná cena', example: '5 500 000 Kč' },
      { key: 'valuation.minPrice', label: 'Minimální cena', example: '5 000 000 Kč' },
      { key: 'valuation.maxPrice', label: 'Maximální cena', example: '6 000 000 Kč' },
      { key: 'valuation.priceRange', label: 'Cenové rozpětí', example: '5 000 000 – 6 000 000 Kč' },
      { key: 'valuation.avgPriceM2', label: 'Cena za m²', example: '73 333 Kč' },
    ],
  },
  {
    category: 'Hypotéka',
    icon: '🏦',
    variables: [
      { key: 'mortgage.propertyPrice', label: 'Cena nemovitosti', example: '5 000 000 Kč' },
      { key: 'mortgage.loanAmount', label: 'Výše úvěru', example: '4 000 000 Kč' },
      { key: 'mortgage.monthlyPayment', label: 'Měsíční splátka', example: '18 500 Kč' },
      { key: 'mortgage.interestRate', label: 'Úroková sazba', example: '5.5' },
      { key: 'mortgage.loanTerm', label: 'Doba splácení (let)', example: '30' },
      { key: 'mortgage.ltv', label: 'LTV (%)', example: '80' },
    ],
  },
  {
    category: 'Projekt',
    icon: '📁',
    variables: [
      { key: 'project.name', label: 'Název projektu', example: 'RealVisor' },
      { key: 'project.website', label: 'Web', example: 'https://realvisor.cz' },
      { key: 'project.phone', label: 'Telefon', example: '+420 123 456 789' },
      { key: 'project.email', label: 'Email', example: 'info@realvisor.cz' },
    ],
  },
  {
    category: 'Agent',
    icon: '👔',
    variables: [
      { key: 'agent.name', label: 'Jméno makléře', example: 'Petr Svoboda' },
      { key: 'agent.phone', label: 'Telefon makléře', example: '+420 987 654 321' },
      { key: 'agent.email', label: 'Email makléře', example: 'petr@realvisor.cz' },
    ],
  },
  {
    category: 'Systém',
    icon: '⚙️',
    variables: [
      { key: 'currentDate', label: 'Aktuální datum', example: '13. 12. 2024' },
      { key: 'currentYear', label: 'Aktuální rok', example: '2024' },
      { key: 'unsubscribeUrl', label: 'Odkaz pro odhlášení', example: 'https://...' },
    ],
  },
];

// ============================================
// TYPY BLOKŮ
// ============================================

const BLOCK_TYPES = [
  { type: 'heading', label: 'Nadpis', icon: 'H' },
  { type: 'paragraph', label: 'Odstavec', icon: '¶' },
  { type: 'button', label: 'Tlačítko', icon: '▢' },
  { type: 'divider', label: 'Oddělovač', icon: '—' },
  { type: 'spacer', label: 'Mezera', icon: '↕' },
  { type: 'valuation-card', label: 'Karta ocenění', icon: '💰' },
  { type: 'property-card', label: 'Karta nemovitosti', icon: '🏠' },
  { type: 'mortgage-card', label: 'Karta hypotéky', icon: '🏦' },
  { type: 'cta-meeting', label: 'CTA schůzka', icon: '📅' },
  { type: 'signature', label: 'Podpis', icon: '✍️' },
];

// ============================================
// KOMPONENTA
// ============================================

export function EmailTemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentProject } = useProject();
  
  const isNew = id === 'new';
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'transactional' as const,
    subject: '',
    preheader: '',
    replyTo: '',
    fromName: '',
    isActive: true,
  });
  
  const [blocks, setBlocks] = useState<EmailBlock[]>([]);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Načíst šablonu
  const { data: template, isLoading } = useQuery({
    queryKey: ['email-template', id],
    queryFn: () => api.get<EmailTemplate>(`/email-templates/${id}`),
    enabled: !isNew && !!id,
  });

  // Naplnit formulář daty
  useEffect(() => {
    if (template) {
      setFormData({
        code: template.code,
        name: template.name,
        description: template.description || '',
        category: template.category,
        subject: template.subject,
        preheader: template.preheader || '',
        replyTo: template.replyTo || '',
        fromName: template.fromName || '',
        isActive: template.isActive,
      });
      setBlocks(template.blocks || []);
    }
  }, [template]);

  // Mutace
  const saveMutation = useMutation({
    mutationFn: (data: any) => {
      if (isNew) {
        return api.post('/email-templates', { ...data, projectId: currentProject?.id });
      }
      return api.put(`/email-templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      navigate('/email/templates');
    },
  });

  const testMutation = useMutation({
    mutationFn: (email: string) => api.post(`/email-templates/${id}/test`, { email }),
    onSuccess: () => {
      setShowTestModal(false);
      setTestEmail('');
    },
  });

  // Handlery
  const handleSave = () => {
    saveMutation.mutate({
      ...formData,
      blocks,
    });
  };

  const addBlock = (type: string) => {
    const newBlock: EmailBlock = {
      id: Math.random().toString(36).substring(2, 9),
      type,
      content: type === 'heading' ? 'Nový nadpis' : type === 'paragraph' ? 'Nový odstavec...' : '',
      props: getDefaultProps(type),
    };
    setBlocks([...blocks, newBlock]);
    setSelectedBlockIndex(blocks.length);
  };

  const getDefaultProps = (type: string): Record<string, unknown> => {
    switch (type) {
      case 'heading':
        return { level: 1, align: 'left' };
      case 'paragraph':
        return { align: 'left' };
      case 'button':
        return { url: '{{project.website}}', style: 'primary', align: 'center' };
      case 'spacer':
        return { height: 20 };
      case 'cta-meeting':
        return { text: 'Chcete se dozvědět více?', buttonText: 'Domluvit schůzku', buttonUrl: '{{project.website}}/schuzka' };
      case 'signature':
        return { showAgentName: true, showProjectName: true, showPhone: true, showEmail: true };
      default:
        return {};
    }
  };

  const updateBlock = (index: number, updates: Partial<EmailBlock>) => {
    const newBlocks = [...blocks];
    const currentBlock = newBlocks[index];
    if (currentBlock) {
      newBlocks[index] = { ...currentBlock, ...updates };
      setBlocks(newBlocks);
    }
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setBlocks(newBlocks);
    setSelectedBlockIndex(newIndex);
  };

  const deleteBlock = (index: number) => {
    setBlocks(blocks.filter((_, i) => i !== index));
    setSelectedBlockIndex(null);
  };

  const insertVariable = (variable: string, target: 'subject' | 'content') => {
    const varString = `{{${variable}}}`;
    
    if (target === 'subject') {
      setFormData({ ...formData, subject: formData.subject + varString });
    } else if (selectedBlockIndex !== null) {
      const block = blocks[selectedBlockIndex];
      updateBlock(selectedBlockIndex, {
        content: (block.content || '') + varString,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/email/templates')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold">
              {isNew ? 'Nová šablona' : formData.name || 'Upravit šablonu'}
            </h1>
            <p className="text-sm text-gray-500">
              {isNew ? 'Vytvořte novou emailovou šablonu' : `Kód: ${formData.code}`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowVariables(!showVariables)}
          >
            <InformationCircleIcon className="h-5 w-5 mr-2" />
            Proměnné
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
          >
            <EyeIcon className="h-5 w-5 mr-2" />
            Náhled
          </Button>
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => setShowTestModal(true)}
            >
              <PaperAirplaneIcon className="h-5 w-5 mr-2" />
              Test
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Ukládám...' : 'Uložit'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Bloky */}
        <div className="w-64 border-r bg-gray-50 p-4 overflow-y-auto">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Přidat blok</h3>
          <div className="grid grid-cols-2 gap-2">
            {BLOCK_TYPES.map((blockType) => (
              <button
                key={blockType.type}
                onClick={() => addBlock(blockType.type)}
                className="p-3 bg-white border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
              >
                <span className="text-xl block mb-1">{blockType.icon}</span>
                <span className="text-xs text-gray-600">{blockType.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Základní info */}
          <Card className="p-4 mb-6">
            <h3 className="font-medium mb-4">Základní informace</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Název šablony"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Např. Výsledek ocenění"
              />
              <Input
                label="Kód (unikátní)"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Např. valuation-result"
                disabled={!isNew}
              />
              <div className="col-span-2">
                <Input
                  label="Popis"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Popis šablony pro interní použití"
                />
              </div>
              <Select
                label="Kategorie"
                value={formData.category}
                onChange={(value) => setFormData({ ...formData, category: value as any })}
                options={[
                  { value: 'transactional', label: 'Transakční' },
                  { value: 'sequence', label: 'Sekvence' },
                  { value: 'manual', label: 'Ruční' },
                  { value: 'system', label: 'Systémový' },
                ]}
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Aktivní
                </label>
              </div>
            </div>
          </Card>

          {/* Předmět a preheader */}
          <Card className="p-4 mb-6">
            <h3 className="font-medium mb-4">Předmět emailu</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Předmět
                  <button
                    onClick={() => setShowVariables(true)}
                    className="ml-2 text-blue-600 text-xs hover:underline"
                  >
                    + Vložit proměnnou
                  </button>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Např. Odhad ceny: {{property.address}}"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <Input
                label="Preheader (náhled v inboxu)"
                value={formData.preheader}
                onChange={(e) => setFormData({ ...formData, preheader: e.target.value })}
                placeholder="Krátký text zobrazený v náhledu emailu"
              />
            </div>
          </Card>

          {/* Bloky obsahu */}
          <Card className="p-4 mb-6">
            <h3 className="font-medium mb-4">Obsah emailu</h3>
            
            {blocks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Zatím žádné bloky</p>
                <p className="text-sm">Přidejte bloky z panelu vlevo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blocks.map((block, index) => (
                  <div
                    key={block.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedBlockIndex === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedBlockIndex(index)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="default">{block.type}</Badge>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveBlock(index, 'up'); }}
                          disabled={index === 0}
                          className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveBlock(index, 'down'); }}
                          disabled={index === blocks.length - 1}
                          className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteBlock(index); }}
                          className="p-1 hover:bg-red-100 text-red-600 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Block content preview */}
                    {block.content && (
                      <p className="text-sm text-gray-600 mb-2">
                        {block.content.length > 100 ? block.content.substring(0, 100) + '...' : block.content}
                      </p>
                    )}
                    
                    {/* Block editor based on type - shown when selected */}
                    {selectedBlockIndex === index && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <BlockEditor
                          block={block}
                          onChange={(updates) => updateBlock(index, updates)}
                          onInsertVariable={(v) => insertVariable(v, 'content')}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Variables Panel */}
        {showVariables && (
          <div className="w-80 border-l bg-white p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Dostupné proměnné</h3>
              <button
                onClick={() => setShowVariables(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Klikněte na proměnnou pro vložení do vybraného bloku nebo předmětu.
            </p>
            
            {AVAILABLE_VARIABLES.map((group) => (
              <div key={group.category} className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {group.icon} {group.category}
                </h4>
                <div className="space-y-1">
                  {group.variables.map((variable) => (
                    <button
                      key={variable.key}
                      onClick={() => insertVariable(variable.key, selectedBlockIndex !== null ? 'content' : 'subject')}
                      className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <div className="font-mono text-blue-600 text-xs">
                        {`{{${variable.key}}}`}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">{variable.label}</span>
                        <span className="text-gray-400 text-xs">{variable.example}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview Panel */}
        {showPreview && (
          <div className="w-96 border-l bg-gray-100 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Náhled emailu</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <p className="text-sm text-gray-500">Předmět:</p>
                <p className="font-medium">{formData.subject || '(prázdný)'}</p>
              </div>
              <div className="p-4">
                <EmailPreview blocks={blocks} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test Email Modal */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title="Odeslat testovací email"
      >
        <div className="space-y-4">
          <Input
            label="Email pro test"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="vas@email.cz"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowTestModal(false)}>
              Zrušit
            </Button>
            <Button
              onClick={() => testMutation.mutate(testEmail)}
              disabled={!testEmail || testMutation.isPending}
            >
              {testMutation.isPending ? 'Odesílám...' : 'Odeslat test'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ============================================
// BLOCK EDITOR KOMPONENTA
// ============================================

function BlockEditor({
  block,
  onChange,
  onInsertVariable,
}: {
  block: EmailBlock;
  onChange: (updates: Partial<EmailBlock>) => void;
  onInsertVariable: (variable: string) => void;
}) {
  const props = block.props || {};

  switch (block.type) {
    case 'heading':
      return (
        <div className="space-y-3">
          <textarea
            value={block.content || ''}
            onChange={(e) => onChange({ content: e.target.value })}
            placeholder="Text nadpisu..."
            className="w-full px-3 py-2 border rounded-lg resize-none"
            rows={2}
          />
          <div className="flex gap-2">
            <Select
              label="Úroveň"
              value={String(props.level || 1)}
              onChange={(value) => onChange({ props: { ...props, level: Number(value) } })}
              options={[
                { value: '1', label: 'H1' },
                { value: '2', label: 'H2' },
                { value: '3', label: 'H3' },
              ]}
            />
            <Select
              label="Zarovnání"
              value={String(props.align || 'left')}
              onChange={(value) => onChange({ props: { ...props, align: value } })}
              options={[
                { value: 'left', label: 'Vlevo' },
                { value: 'center', label: 'Na střed' },
                { value: 'right', label: 'Vpravo' },
              ]}
            />
          </div>
        </div>
      );

    case 'text':
    case 'paragraph':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Text
              <button
                onClick={() => onInsertVariable('contact.firstName')}
                className="ml-2 text-blue-600 text-xs hover:underline"
              >
                + Proměnná
              </button>
            </label>
            <textarea
              value={block.content || ''}
              onChange={(e) => onChange({ content: e.target.value })}
              placeholder="Text odstavce... Použijte **tučně** pro zvýraznění."
              className="w-full px-3 py-2 border rounded-lg resize-none"
              rows={4}
            />
          </div>
          <Select
            label="Zarovnání"
            value={String(props.align || 'left')}
            onChange={(value) => onChange({ props: { ...props, align: value } })}
            options={[
              { value: 'left', label: 'Vlevo' },
              { value: 'center', label: 'Na střed' },
              { value: 'right', label: 'Vpravo' },
            ]}
          />
        </div>
      );

    case 'button':
      return (
        <div className="space-y-3">
          <Input
            label="Text tlačítka"
            value={block.content || ''}
            onChange={(e) => onChange({ content: e.target.value })}
            placeholder="Např. Domluvit schůzku"
          />
          <Input
            label="URL odkazu"
            value={String(props.url || '')}
            onChange={(e) => onChange({ props: { ...props, url: e.target.value } })}
            placeholder="https://... nebo {{project.website}}"
          />
          <Select
            label="Styl"
            value={String(props.style || 'primary')}
            onChange={(value) => onChange({ props: { ...props, style: value } })}
            options={[
              { value: 'primary', label: 'Primární (modrý)' },
              { value: 'secondary', label: 'Sekundární (šedý)' },
              { value: 'success', label: 'Úspěch (zelený)' },
            ]}
          />
        </div>
      );

    case 'spacer':
      return (
        <Input
          label="Výška (px)"
          type="number"
          value={String(props.height || 20)}
          onChange={(e) => onChange({ props: { ...props, height: Number(e.target.value) } })}
        />
      );

    case 'cta':
    case 'cta-meeting':
      return (
        <div className="space-y-3">
          <Input
            label="Text nad tlačítkem"
            value={String(props.text || '')}
            onChange={(e) => onChange({ props: { ...props, text: e.target.value } })}
            placeholder="Chcete se dozvědět více?"
          />
          <Input
            label="Text tlačítka"
            value={String(props.buttonText || '')}
            onChange={(e) => onChange({ props: { ...props, buttonText: e.target.value } })}
            placeholder="Domluvit schůzku"
          />
          <Input
            label="URL tlačítka"
            value={String(props.buttonUrl || '')}
            onChange={(e) => onChange({ props: { ...props, buttonUrl: e.target.value } })}
            placeholder="{{project.website}}/schuzka"
          />
        </div>
      );

    case 'signature':
      return (
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(props.showAgentName)}
              onChange={(e) => onChange({ props: { ...props, showAgentName: e.target.checked } })}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Zobrazit jméno makléře</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(props.showProjectName)}
              onChange={(e) => onChange({ props: { ...props, showProjectName: e.target.checked } })}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Zobrazit název projektu</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(props.showPhone)}
              onChange={(e) => onChange({ props: { ...props, showPhone: e.target.checked } })}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Zobrazit telefon</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(props.showEmail)}
              onChange={(e) => onChange({ props: { ...props, showEmail: e.target.checked } })}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Zobrazit email</span>
          </label>
        </div>
      );

    case 'valuation-card':
    case 'property-card':
    case 'mortgage-card':
      return (
        <p className="text-sm text-gray-500">
          Tento blok se automaticky naplní daty z formuláře.
        </p>
      );

    case 'divider':
      return (
        <p className="text-sm text-gray-500">
          Horizontální oddělovač - bez nastavení.
        </p>
      );

    default:
      return null;
  }
}

// ============================================
// EMAIL PREVIEW KOMPONENTA
// ============================================

function EmailPreview({ blocks }: { blocks: EmailBlock[] }) {
  const renderBlock = (block: EmailBlock) => {
    const props = block.props || {};
    
    switch (block.type) {
      case 'heading':
        const HeadingTag = `h${props.level || 1}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag
            className={`font-bold mb-4 ${
              props.level === 1 ? 'text-2xl' : props.level === 2 ? 'text-xl' : 'text-lg'
            }`}
            style={{ textAlign: props.align as any }}
          >
            {block.content || 'Nadpis'}
          </HeadingTag>
        );

      case 'paragraph':
        return (
          <p
            className="mb-4 text-gray-700 whitespace-pre-wrap"
            style={{ textAlign: props.align as any }}
          >
            {block.content || 'Odstavec...'}
          </p>
        );

      case 'button':
        return (
          <div className="mb-4" style={{ textAlign: props.align as any }}>
            <span className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-medium">
              {block.content || 'Tlačítko'}
            </span>
          </div>
        );

      case 'divider':
        return <hr className="my-6 border-gray-200" />;

      case 'spacer':
        return <div style={{ height: Number(props.height) || 20 }} />;

      case 'valuation-card':
        return (
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 text-center mb-4">
            <p className="text-sm opacity-90">Odhadovaná cena</p>
            <p className="text-3xl font-bold">5 500 000 Kč</p>
            <p className="text-sm opacity-90">5 000 000 – 6 000 000 Kč</p>
          </div>
        );

      case 'property-card':
        return (
          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <p className="font-medium">Václavské náměstí 1, Praha</p>
            <p className="text-sm text-gray-600">3+kk • 75 m²</p>
          </div>
        );

      case 'mortgage-card':
        return (
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 text-center mb-4">
            <p className="text-sm opacity-90">Měsíční splátka</p>
            <p className="text-3xl font-bold">18 500 Kč</p>
            <div className="flex justify-around mt-4 text-sm">
              <div>
                <p className="opacity-80">Úvěr</p>
                <p className="font-semibold">4 000 000 Kč</p>
              </div>
              <div>
                <p className="opacity-80">Úrok</p>
                <p className="font-semibold">5.5 %</p>
              </div>
              <div>
                <p className="opacity-80">Doba</p>
                <p className="font-semibold">30 let</p>
              </div>
            </div>
          </div>
        );

      case 'cta-meeting':
        return (
          <div className="bg-gray-100 rounded-lg p-6 text-center mb-4">
            <p className="font-medium mb-3">{String(props.text) || 'Chcete se dozvědět více?'}</p>
            <span className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-medium">
              {String(props.buttonText) || 'Domluvit schůzku'}
            </span>
          </div>
        );

      case 'signature':
        return (
          <div className="border-t pt-4 mt-6">
            <p>S pozdravem,</p>
            {props.showAgentName && <p className="font-medium">Jan Novák</p>}
            {props.showProjectName && <p className="text-gray-600">RealVisor</p>}
            {props.showPhone && <p className="text-sm text-gray-500">📞 +420 123 456 789</p>}
            {props.showEmail && <p className="text-sm text-gray-500">✉️ info@realvisor.cz</p>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="font-sans text-gray-900">
      {blocks.length === 0 ? (
        <p className="text-gray-400 text-center py-8">Prázdný email</p>
      ) : (
        blocks.map((block) => (
          <div key={block.id}>{renderBlock(block)}</div>
        ))
      )}
    </div>
  );
}

export default EmailTemplateEditor;
